import { DOCUMENT } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc } from '@kanalytics';
import { BaseAuthenticationBloc } from '@kauth';
import { NavigationBloc, SharingBloc, SubscriptionBloc, TagBloc } from '@kbloc';
import { EnvironmentVariablesService } from '@kenv';
import { UserBloc } from '@kp/user/user.bloc';
import { DataStoreService } from '@kservice';
import { AppSwitchLocation } from '@ktypes/enums';
import { NavItemType, ProductInfo, Status } from '@ktypes/models';
import { MockComponent } from '@kutil/test';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-site-nav',
  templateUrl: './site-nav.component.html',
  styleUrls: ['./site-nav.component.scss'],
})
export class SiteNavComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private _analyticsBloc: AnalyticsBloc,
    private _authBloc: BaseAuthenticationBloc,
    public dataStore: DataStoreService,
    @Inject(DOCUMENT) private _document: Document,
    private _environmentVariablesService: EnvironmentVariablesService,
    public navBloc: NavigationBloc,
    private _router: Router,
    public sharingBloc: SharingBloc,
    private _subscriptionBloc: SubscriptionBloc,
    public tagBloc: TagBloc,
    private _userBloc: UserBloc
  ) {}

  @Input() navActive: boolean;
  @Output() sidebarClick = new EventEmitter<boolean>();

  readonly moreApps = toSignal(this.navBloc.moreApps$);
  navType = NavItemType;
  sideMenuAnnouncement: string;
  showPremiumMenuItem = false;

  private _destroy$ = new Subject<void>();
  private _navigated = false;

  @HostListener('window:keydown.escape', ['$event']) private _triggerEsc() {
    if (this.navActive) {
      this.closeOverlay(true);
    }
  }

  ngOnInit() {
    this.routeEvent(this._router);
    this.sharingBloc.getInvites();
    this._subscriptionBloc.getSubscriptionStatus();
    this._subscriptionBloc.subscriptionStatus$.pipe(takeUntil(this._destroy$)).subscribe((status) => {
      if (status?.status !== Status.done) {
        return;
      }
      const hasAccess = status.data?.canonicalHasAccess === true;
      const canPurchase = status.data?.canonicalCanPurchase === true;
      this.showPremiumMenuItem = !hasAccess && canPurchase;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['navActive'] != null &&
      !changes['navActive'].firstChange &&
      changes['navActive'].previousValue !== changes['navActive'].currentValue &&
      !this._navigated
    ) {
      /**
       * t(Opening side navigation)
       * t(Closing side navigation)
       */
      this.sideMenuAnnouncement = `${this.navActive ? 'Opening' : 'Closing'} side navigation`;
    } else if (this._navigated) {
      this._navigated = false;
    }
  }

  routeEvent(router: Router) {
    router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.sidebarClick.emit(true);
        this.sideMenuAnnouncement = '';
        this._navigated = true;
      }
    });
  }

  onSidebarClick(e: Event) {
    const element = e.target as HTMLElement;
    if (element.tagName !== 'A' && !element.closest('a')) {
      this.closeOverlay();
      e.preventDefault();
    } else if (element.tagName === 'A') {
      element.blur();
    } else {
      element.closest('a').blur();
    }
  }

  closeOverlay(shouldClose = false) {
    if (this.navActive && shouldClose) {
      (this._document.querySelectorAll('.nav-menu-button')?.[0] as HTMLElement).focus();
    }
    this.sidebarClick.emit(shouldClose);
  }

  trackEvent(app: ProductInfo) {
    const event = this._analyticsBloc.createEventFromInteraction({
      event: AnalyticEvent.app_switch_requested,
      meta: {
        app_requested: app.product,
        link_location: AppSwitchLocation.menu,
        source_app: this._environmentVariablesService.product,
      },
    });
    this._analyticsBloc.sendEvent(event);
    this._userBloc.switchApplication(app.product);
    this.closeOverlay();
  }

  setPremiumPaywallContext(event?: Event) {
    event?.preventDefault();

    const status = this._subscriptionBloc.subscriptionStatus?.data;
    if (!status) {
      // Status can still be loading when menu is opened; refresh and open overlay when ready.
      this._subscriptionBloc.getSubscriptionStatus(0, { forceRefresh: true });
      this._subscriptionBloc.subscriptionStatus$
        .pipe(
          filter((subscriptionStatus) => subscriptionStatus?.status === Status.done),
          take(1),
          takeUntil(this._destroy$)
        )
        .subscribe((subscriptionStatus) => {
          const refreshedStatus = subscriptionStatus?.data;
          if (
            !refreshedStatus ||
            refreshedStatus.canonicalHasAccess ||
            !refreshedStatus.canonicalCanPurchase ||
            !refreshedStatus.canonicalOfferingId
          ) {
            return;
          }
          this._authBloc.setPremiumPaywallContext(refreshedStatus.canonicalOfferingId, this._router.url || '/today');
          this.closeOverlay(true);
        });
      return;
    }

    if (status.canonicalHasAccess || !status.canonicalCanPurchase || !status.canonicalOfferingId) {
      return;
    }

    this._authBloc.setPremiumPaywallContext(status.canonicalOfferingId, this._router.url || '/today');
    this.closeOverlay(true);
  }

  sideNavTab(e: Event) {
    if (this.navActive) {
      (this._document.querySelectorAll('.nav-menu-button')?.[0] as HTMLElement).focus();
      e.preventDefault();
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }
}

export const MockSiteNavComponent = MockComponent({
  selector: 'kp-site-nav',
  inputs: ['navActive'],
});
